import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["title", "subtitle", "body", "footer", "headerButtons", "className", "bodyClassName", "footerClassName", "showCloseButton", "useOverflow", "bsSize", "show", "onHide", "disableEsc", "onEsc", "onCloseValidation"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import activeElement from 'dom-helpers/activeElement';
import { CSSTransition } from 'react-transition-group';
import { getOrCreatePortalRoot } from '../../utils/portalRoot';
import useEsc from '../../hooks/useEsc';
import useAfterMount from '../../hooks/useAfterMount';
import DialogHeader from './DialogHeader';
import DialogBody from './DialogBody';
import DialogFooter from './DialogFooter';
import baseDialogPropTypes from './baseDialogPropTypes';
var MODAL_DIALOG_CLASS = 'modal-dialog';
var MODAL_OPEN_CLASS = 'modal-open';
var Dialog = function Dialog(props) {
  var title = props.title,
    subtitle = props.subtitle,
    body = props.body,
    footer = props.footer,
    headerButtons = props.headerButtons,
    className = props.className,
    bodyClassName = props.bodyClassName,
    footerClassName = props.footerClassName,
    showCloseButton = props.showCloseButton,
    useOverflow = props.useOverflow,
    bsSize = props.bsSize,
    show = props.show,
    onHide = props.onHide,
    disableEsc = props.disableEsc,
    onEsc = props.onEsc,
    onCloseValidation = props.onCloseValidation,
    remainingProps = _objectWithoutProperties(props, _excluded);
  var _useState = useState(show),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpen = _useState2[1];
  var dialogWrapperRef = useRef();
  var modalRoot = getOrCreatePortalRoot();
  var toggleBodyClass = function toggleBodyClass(add) {
    // We need to set a body class to fix the -webkit-overflow-scrolling on safari and iOS

    // Remove "modal-open" from body only when there is no other dialog in the DOM
    // in order to support multiple dialogs
    var hasOtherDialogs = modalRoot.getElementsByClassName(MODAL_DIALOG_CLASS).length > 1;
    if (add) {
      document.body.classList.add(MODAL_OPEN_CLASS);
    } else if (!add && !hasOtherDialogs) {
      document.body.classList.remove(MODAL_OPEN_CLASS);
    }
  };
  var handleClose = function handleClose(usedEscapeKey) {
    if (onCloseValidation()) {
      setOpen(false);
      onHide();
      usedEscapeKey && onEsc();
    }
  };
  useAfterMount(function () {
    setOpen(show);
    toggleBodyClass(show);
  }, [show]);
  useEsc(function () {
    if (!dialogWrapperRef || !dialogWrapperRef.current) {
      return;
    }
    var dialogElement = dialogWrapperRef.current;
    var currentActiveElement = activeElement(document);

    // Only allow to close the dialog when the focus is inside the dialog
    if (!disableEsc && dialogElement.contains(currentActiveElement)) {
      handleClose(true);
    }
  });
  var handleEntered = function handleEntered() {
    // Note that in order to focus the dialog itself, the tabindex has to be set on that element
    dialogWrapperRef && dialogWrapperRef.current && dialogWrapperRef.current.focus();
  };
  var modalClasses = classNames('modal', 'show', className);
  var modalDialogClasses = classNames(MODAL_DIALOG_CLASS, useOverflow && 'modal-overflow', bsSize === Dialog.SIZE_SM && 'modal-sm', bsSize === Dialog.SIZE_LG && 'modal-lg', bsSize === Dialog.SIZE_XL && 'modal-xl', bsSize === Dialog.SIZE_FULL && 'modal-full-width', bsSize === Dialog.SIZE_FULL_WIDTH && 'modal-full-width', bsSize === Dialog.SIZE_FULL_HEIGHT && 'modal-full-height', bsSize === Dialog.SIZE_FULL_SCREEN && 'modal-fullscreen');
  var backdropClasses = classNames('modal-backdrop');
  return /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/React.createElement(CSSTransition, {
    "in": open,
    timeout: 200,
    classNames: "modal",
    unmountOnExit: true,
    onEntered: handleEntered
  }, /*#__PURE__*/React.createElement("div", _extends({}, remainingProps, {
    className: modalClasses,
    tabIndex: 1,
    role: "dialog",
    ref: dialogWrapperRef
  }), /*#__PURE__*/React.createElement("div", {
    className: modalDialogClasses,
    role: "document"
  }, /*#__PURE__*/React.createElement("div", {
    className: "modal-content"
  }, title && /*#__PURE__*/React.createElement(DialogHeader, {
    title: title,
    subtitle: subtitle,
    headerButtons: headerButtons,
    showCloseButton: showCloseButton,
    onClose: handleClose
  }), body && /*#__PURE__*/React.createElement(DialogBody, {
    className: bodyClassName
  }, body), footer && /*#__PURE__*/React.createElement(DialogFooter, {
    className: footerClassName
  }, footer))), /*#__PURE__*/React.createElement("div", {
    className: backdropClasses
  }))), modalRoot);
};
Dialog.SIZE_SM = 'sm';
Dialog.SIZE_MD = 'md'; // default
Dialog.SIZE_LG = 'lg';
Dialog.SIZE_XL = 'xl';
Dialog.SIZE_FULL = 'full';
Dialog.SIZE_FULL_WIDTH = 'fullwidth';
Dialog.SIZE_FULL_HEIGHT = 'fullheight';
Dialog.SIZE_FULL_SCREEN = 'fullscreen';
Dialog.defaultProps = {
  show: false,
  onHide: function onHide() {},
  onEsc: function onEsc() {},
  className: '',
  showCloseButton: true,
  disableEsc: false,
  useOverflow: false,
  onCloseValidation: function onCloseValidation() {
    return true;
  }
};
Dialog.propTypes = _objectSpread(_objectSpread({}, baseDialogPropTypes), {}, {
  body: PropTypes.node,
  footer: PropTypes.node,
  headerButtons: PropTypes.node,
  className: PropTypes.string,
  bodyClassName: PropTypes.string,
  footerClassName: PropTypes.string,
  onHide: PropTypes.func,
  onEsc: PropTypes.func
});
export default Dialog;